import { useState, useRef, useEffect, useCallback } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { useSyncState } from 'shared/hooks/useSyncState';
import { TableContent } from 'entities/table/ui/TableContent';
import { useGetUsersQuery } from 'features/getUsers/api/getUsersApi';
import { prepareList } from 'features/getUsers/model/prepareList';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from 'app/store';
import { setHeaderTitle } from 'features/ui/model/uiSlice';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useGetCountriesQuery } from 'features/getCountries/api/getCountriesApi';
import { CitiesListType } from 'features/getCities/types';
import { UsersDrawer } from 'widgets/users/ui/UsersDrawer';

const fields = [
  {
    field: 'id',
    title: 'ID',
    width: '5%',
  },
  {
    field: 'phone',
    title: 'Телефон',
    width: '10%',
  },
  {
    field: 'phone',
    title: 'Логин',
    width: '15%',
  },
  {
    field: 'name',
    title: 'ФИО',
    width: '20%',
  },
  {
    field: 'city',
    title: 'Город и страна',
    width: '10%',
  },
  {
    field: 'iin',
    title: 'ИИН',
    width: '10%',
  },
  {
    field: 'status',
    title: 'Статус',
    width: '10%',
  },
];
type Order = 'asc' | 'desc';
type Filter = {
  city: Array<string | number>;
  country: Array<string | number>;
};

export const UsersList = () => {
  const currentLocation = useLocation().pathname;
  const pathnameArray = currentLocation.split('/');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { app_type } = useParams();
  const [pagination, setPagination] = useState<{
    page: number;
    count: number;
    search: string;
  }>({
    page: 1,
    count: 10,
    search: '',
  });
  const [isShowSearch, setIsShowSearch] = useState(false);
  const searchQuery = useSyncState('');
  const searchRef = useRef('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('id');

  const handleSetSearch = (search: string) => {
    const onlyNumbers = search.split('+');
    const searchValue = onlyNumbers.length === 2 ? onlyNumbers[1] : onlyNumbers[0];

    searchQuery.set(searchValue);
    searchRef.current = searchValue;
  };

  const [filter, setFilter] = useState<Filter>({
    city: [],
    country: [],
  });

  const { data: usersList, isSuccess } = useGetUsersQuery({
    app_type: app_type!,
    page: pagination.page,
    size: pagination.count,
    search: pagination.search,
    city: filter.city,
    country: filter.country,
    sort: orderBy,
    sort_desc: order === 'desc',
  });

  const { data: cityList } = useGetCitiesQuery({});
  const { data: countryList } = useGetCountriesQuery(null);
  const handleUserSelect = (id: number) => {
    navigate(`/users-mp/${app_type}/${id}`);
  };

  function handleSearch() {
    setPagination((prev) => ({
      ...prev,
      search: searchRef.current,
    }));
  }

  const handleClearFilter = () => {
    setFilter((prev) => ({
      ...prev,
      city: [],
      country: [],
    }));
  };
  const handleClearSearch = () => {
    setIsShowSearch(false);
    searchQuery.set('');
    searchRef.current = '';
    setPagination((prev) => ({
      ...prev,
      search: '',
    }));
  };
  const filterCitiesByCountry = useCallback(() => {
    const filteredCities: any = [];
    cityList?.map((city) => {
      filter.country.map((id: any) => {
        if (city.country_id === id) {
          filteredCities.push(city);
        }
      });
    });

    return filteredCities;
  }, [filter.country]);

  // TODO: переписать универсально
  const handleApplyFilter = (field: keyof Filter, value: string | number) => {
    setFilter((prev: any) => {
      if (prev[field].includes(value)) {
        return {
          ...prev,
          [field]: prev[field].filter((item: any) => item !== value),
        };
      }

      return {
        ...prev,
        [field]: [...prev[field], value],
      };
    });
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: any) => {
    const isAsc = orderBy === property && order === 'asc';

    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if (app_type) {
      dispatch(setHeaderTitle(app_type!.toUpperCase()));
    }
  }, [app_type]);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          margin: '0 0 15px 0',
        }}
      >
        <Link
          color="inherit"
          href="widgets/users/ui/UsersList#"
        >
          Пользователи
        </Link>
        <Link
          color="inherit"
          href="widgets/users/ui/UsersList#"
        >
          Пользователи МП
        </Link>
        <Link
          color="inherit"
          href="widgets/users/ui/UsersList#"
        >
          {pathnameArray[2]}
        </Link>
      </Breadcrumbs>
      {isSuccess ? (
        <TableContent<Filter>
          searchPlaceholder="Поиск по телефону и ФИО"
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          title={`Список клиентов ${app_type?.toUpperCase()}`}
          onSearch={() => handleSearch()}
          applyFilter={handleApplyFilter}
          fields={fields}
          list={prepareList(usersList)}
          count={usersList.total}
          page={pagination.page - 1}
          perPage={pagination.count}
          onPageChange={(_, page) => {
            setPagination((prev) => ({
              ...prev,
              page: page + 1,
            }));
          }}
          onRowsPerPageChange={(e) => {
            setPagination(() => ({
              page: 1,
              count: parseInt(e.target.value),
              search: pagination.search,
            }));
          }}
          onRowClick={handleUserSelect}
          isShowSearch={isShowSearch}
          onShowSearch={(showSearch) => setIsShowSearch(showSearch)}
          onClearSearch={handleClearSearch}
          onClearFilter={handleClearFilter}
          search={searchQuery.get()}
          setSearch={handleSetSearch}
          filterFields={[
            {
              title: 'Страна',
              field: 'country',
              list: countryList && countryList?.map((item) => ({ title: item.name, value: item.id })),
            },
            {
              title: 'Город',
              field: 'city',
              list:
                cityList && filter.country.length > 0
                  ? filterCitiesByCountry().map((item: CitiesListType) => ({
                      title: item.name,
                      value: item.id,
                    }))
                  : cityList?.map((item) => ({ title: item.name, value: item.id })),
            },
          ]}
          filter={filter}
        />
      ) : (
        false
      )}
      <UsersDrawer />
    </>
  );
};
